// Pure greeting helper for the chat home/empty state.
// Resolves a friendly, time-of-day greeting for the signed-in technician with a
// graceful fallback chain so the headline NEVER renders "undefined"/"null":
//   full_name (first token) -> username (if not email-like) -> neutral "Good <tod>".

export type TimeOfDay = 'morning' | 'afternoon' | 'evening';

export interface Greeting {
  timeOfDay: TimeOfDay;
  /** Resolved first name, or null when we have nothing usable. */
  name: string | null;
  /** Ready-to-render headline: "Afternoon, Neal" or neutral "Good afternoon". */
  headline: string;
}

function timeOfDayFor(hour: number): TimeOfDay {
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** First whitespace-delimited token of a trimmed, non-blank string, else null. */
function firstToken(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/)[0];
}

/**
 * @param fullName  the user's `full_name` (may be null/blank)
 * @param now       current time (injected for testability)
 * @param username  optional `username` fallback (ignored if it looks like an email)
 */
export function getGreeting(
  fullName: string | null | undefined,
  now: Date,
  username?: string | null,
): Greeting {
  const timeOfDay = timeOfDayFor(now.getHours());

  let name = firstToken(fullName);
  if (!name) {
    const fromUsername = firstToken(username);
    // Email-like usernames (e.g. "ashankar@air.com") aren't friendly first names.
    if (fromUsername && !fromUsername.includes('@')) {
      name = fromUsername;
    }
  }

  if (name) {
    name = capitalize(name);
    return { timeOfDay, name, headline: `${capitalize(timeOfDay)}, ${name}` };
  }

  return { timeOfDay, name: null, headline: `Good ${timeOfDay}` };
}
